import { ArrowUpRightIcon, CameraIcon, GlobeIcon, VideoIcon } from "lucide-react";
import { InformationText } from "@/components/app/information-text";
import { useT } from "@/i18n";
import {
  informationLinkSite,
  informationUrl,
  type MediaContributor,
  type MediaLink,
  type MediaRole,
} from "../../../shared/tournament-information";

import { InfoDisclosure } from "./InfoDisclosure";

export const mediaRoleKey = {
  photographer: "eventInfo.photographer",
  videographer: "eventInfo.videographer",
  both: "eventInfo.both",
} as const satisfies Record<MediaRole, string>;

export function TournamentMedia({ media }: { media: MediaContributor[] }) {
  const t = useT();
  if (!media.length) return null;
  return (
    <InfoDisclosure
      icon={CameraIcon}
      title={t("eventInfo.media")}
      summary={media.map((person) => person.name || t(mediaRoleKey[person.role])).join(", ")}
    >
      <div className="-mx-5 -my-4 divide-y">
        {media.map((person) => {
          const Icon = person.role === "videographer" ? VideoIcon : CameraIcon;
          return (
            <section key={person.id} className="min-w-0">
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Icon aria-hidden="true" className="mt-1 size-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <h3 className="break-words text-lg font-semibold">
                      {person.name || t(mediaRoleKey[person.role])}
                    </h3>
                    {person.name && (
                      <p className="text-sm text-muted-foreground">
                        {t(mediaRoleKey[person.role])}
                      </p>
                    )}
                  </div>
                </div>
                {person.profiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {person.profiles.map((link) => (
                      <MediaAnchor key={link.id} link={link} compact />
                    ))}
                  </div>
                )}
              </div>
              {person.albums.length > 0 ? (
                <ul className="divide-y border-t">
                  {person.albums.map((link) => (
                    <li key={link.id} className="min-w-0 space-y-2 px-4 py-3">
                      <MediaAnchor link={link} />
                      {link.notes && (
                        <div className="text-muted-foreground">
                          <InformationText text={link.notes} />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="border-t px-4 py-3 text-sm text-muted-foreground">
                  {t("eventInfo.pending")}
                </p>
              )}
            </section>
          );
        })}
      </div>
    </InfoDisclosure>
  );
}

function MediaAnchor({ link, compact = false }: { link: MediaLink; compact?: boolean }) {
  const href = informationUrl(link.url);
  if (!href) return null;
  const site = informationLinkSite(href);
  const Icon = site === "Instagram" ? CameraIcon : site === "YouTube" ? VideoIcon : GlobeIcon;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        compact
          ? "inline-flex min-h-11 max-w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-accent"
          : "flex min-h-11 items-center gap-3 text-brand-ink hover:underline underline-offset-4"
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">
        <span className="font-medium">{link.title || site}</span>
        {!compact && link.title && (
          <span className="block text-xs text-muted-foreground">{site}</span>
        )}
      </span>
      <ArrowUpRightIcon aria-hidden="true" className="ml-auto size-4 shrink-0" />
    </a>
  );
}
